import moment from 'moment'
import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/hi'
import 'moment/locale/ja'
import 'moment/locale/pt-br'
import 'moment/locale/zh-cn'

import { parseStrapiImage } from 'utils'

const notificationLocales = {
    Chinese: 'zh-cn',
    English: 'en',
    German: 'de',
    Hindi: 'hi',
    Japanese: 'ja',
    Portuguese: 'pt-br',
    Spanish: 'es',
}

const unwrapNotification = item => ({
    id: item?.id || item?.notificationId,
    documentId: item?.documentId || item?.attributes?.documentId,
    ...(item?.attributes || item || {}),
})

const normalizeBody = (body, fallbackText) => {
    const source = Array.isArray(body) && body.length
        ? body
        : `${body || fallbackText || ''}`.split(/\n{2,}/)

    return source
        .map((item, index) => ({
            key: `${item?.key || item?.id || index}`,
            text: `${typeof item === 'string' ? item : item?.text || item?.body || item?.content || ''}`.trim(),
        }))
        .filter(item => item.text)
}

const getImageUrl = image => {
    const entity = image?.data?.attributes || image?.attributes || image?.data || image
    const url = entity?.formats?.small?.url || entity?.formats?.thumbnail?.url || entity?.url
    return url ? parseStrapiImage(url) : null
}

export const normalizeNotificationUrl = value => {
    const url = `${value || ''}`.trim()

    if (url.startsWith('/') && !url.startsWith('//')) {
        return url
    }

    try {
        const parsedUrl = new URL(url)
        return ['http:', 'https:'].includes(parsedUrl.protocol) ? parsedUrl.toString() : null
    } catch (error) {
        return null
    }
}

export const normalizeNotification = (item, language = 'English') => {
    const notification = unwrapNotification(item)
    const data = notification?.data && typeof notification.data === 'object' ? notification.data : {}
    const createdAt = notification?.createdAt || notification?.created_at || data?.createdAt || null
    const text = notification?.text
        || data?.text
        || (typeof data?.body === 'string' ? data.body : '')
        || (typeof notification?.body === 'string' ? notification.body : '')
    const readAt = notification?.readAt || notification?.read_at || null

    return {
        ...notification,
        id: notification?.id || data?.notificationId,
        documentId: notification?.documentId || data?.documentId || null,
        title: notification?.title || data?.title || 'Notification',
        description: `${data?.description || notification?.description || text}`,
        text,
        body: normalizeBody(data?.body || notification?.body, text),
        createdAt,
        formattedDate: createdAt ? moment(createdAt).locale(notificationLocales[language] || 'en').format('LLL') : '',
        relativeTime: createdAt ? moment(createdAt).locale(notificationLocales[language] || 'en').fromNow() : '',
        category: data?.category || notification?.category || 'Notification',
        icon: data?.icon || notification?.icon || 'notifications',
        tone: data?.tone || notification?.tone || 'blue',
        imageUrl: getImageUrl(data?.image || notification?.image),
        readAt,
        unread: !readAt,
        ctaLabel: data?.ctaLabel || notification?.ctaLabel || null,
        url: normalizeNotificationUrl(notification?.url || data?.url),
    }
}

export const normalizeNotifications = (items, language = 'English') => {
    const notificationIds = new Set()

    return (Array.isArray(items) ? items : [])
        .map(item => normalizeNotification(item, language))
        .filter(item => {
            const key = `${item?.id || item?.documentId || ''}`
            if (!key || notificationIds.has(key)) return false
            notificationIds.add(key)
            return true
        })
}
